/* --------------------------------------------------------------------------
 * itemValue.js — canonical 3-tier fallback for item cost / value / profit.
 *
 *   Order:
 *     1) Dynamic category field on item_data ("Purchase Price" / "Estimated Value")
 *     2) Top-level purchase_price / estimated_value
 *     3) Legacy GST-prefixed columns
 *     4) For estimated value only: fall back to cost so P/L = 0 (rather than $0)
 *
 *   Used by:  Items.jsx, CollectionDetail.jsx, Analytics.jsx
 *   Do NOT duplicate this logic. Import from "@/utils/itemValue".
 * -------------------------------------------------------------------------- */

export const readDynamicNumber = (item_data, ...keys) => {
  for (const k of keys) {
    const v = item_data?.[k];
    if (v === undefined || v === null || v === "") continue;
    const n = Number(v);
    if (!isNaN(n)) return n;
  }
  return NaN;
};

export const itemCost = (it) => {
  // Unified pricing: Buy Price (Inc GST) is the canonical "cost" used by
  // Dashboard rollups (Total Purchase Cost). Falls back to the legacy
  // dynamic field / `purchase_price` / `cost_price_ex_gst` for older items
  // saved before the GST split rolled out.
  const inc = Number(it?.cost_price_inc_gst || 0);
  if (inc) return inc;
  const d = it?.item_data || {};
  const fromDynamic = readDynamicNumber(d, "Purchase Price", "Purchase price", "purchase_price");
  if (!isNaN(fromDynamic)) return fromDynamic;
  const top = Number(it?.purchase_price || 0);
  if (top) return top;
  return Number(it?.cost_price_ex_gst || 0);
};

export const itemValue = (it) => {
  // 2026-02-26 unification — all display / list helpers now derive
  // their "is this item valued?" answer from the canonical
  // `computeItemValuation()` result. An item is considered valued
  // ONLY when it carries a Manual, Market, or Most-Recent-Sale
  // valuation (`includeInValuationTotals === true`). Unvalued
  // items return 0 so downstream row renderers show "Awaiting
  // Valuation" instead of a fabricated per-unit price cascaded
  // from legacy `estimated_value_*` columns. Note: this helper is
  // called from list rows without sales context, so sale-only
  // valued items will read as unvalued here — the aggregate views
  // (Analytics, CollectionDetail KPIs) already use the salesful
  // `_val(it)` map so they remain accurate.
  const v = computeItemValuation(it, []);
  return v.includeInValuationTotals ? v.current_unit_value : 0;
};

/**
 * isValueInferred(item) — returns true when the item has NO Manual,
 * Market, or Most-Recent-Sale valuation. UI uses this to render the
 * "Awaiting Valuation" placeholder (or the dashed-underline hint)
 * instead of a numeric $0. Kept in sync with itemValue() so the flag
 * and the numeric helper never contradict each other.
 */
export const isValueInferred = (it) => {
  const v = computeItemValuation(it, []);
  return !v.includeInValuationTotals;
};

export const itemProfit = (it) => {
  // 2026-02-26 — same gating rule as `itemValue`. Unvalued items
  // return 0 profit so per-row profit columns never show a
  // synthesized loss driven purely by cost against a null value.
  const v = computeItemValuation(it, []);
  return v.includeInValuationTotals ? v.current_unit_value - itemCost(it) : 0;
};

/* --------------------------------------------------------------------------
 * NZD-normalised variants — for cross-currency aggregation.
 *
 * Order of operations: native Ex → convert to NZD via fx → apply NZ GST (15%).
 * This is the canonical Inc-GST NZD amount used by every KPI, rollup and
 * chart. Applying GST AFTER the FX conversion guarantees that Ex-NZD and
 * Inc-NZD always differ by exactly 15% for every item regardless of the
 * item's native tax regime (USD Sales Tax = 0%, GBP/EUR VAT = 20%, etc.).
 *
 * Rounding: every per-item NZD result is quantised through `q2` (2dp,
 * ROUND_HALF_UP). Downstream sums add already-rounded line items —
 * matching invoice-style arithmetic so "Item Detail NZD" always equals
 * the item's contribution to the collection totals.
 *
 * `baseNet` derivation (native Ex):
 *   1. Explicit `cost_price_ex_gst` / `estimated_value_ex_gst` when > 0.
 *   2. Fallback: `..._inc_gst` divided by (1 + native tax rate) so an item
 *      that only stored the Inc value gets un-taxed back to Ex.
 *   3. Legacy last-resort: `itemCost` / `itemValue` divided by native rate.
 *
 * Native tax rates (must match ItemEntry.taxConfig):
 *   NZD → 0.15, USD → 0, GBP/EUR → 0.20, AUD → 0.10, other → 0.
 *
 * Callers: every rollup / KPI / analytics tile that sums across items
 * with heterogeneous currencies. Never call these for per-item native-
 * currency display — use itemCost/itemValue for that so the user sees
 * the amount they actually paid / expect to receive.
 * -------------------------------------------------------------------------- */
import { q2 } from "@/lib/money";

const _num = (v, fallback = 1) => {
  const n = Number(v);
  return isFinite(n) && n > 0 ? n : fallback;
};

// Dynamic tax rate resolver (2026-02 — Step 4 of pricing overhaul).
// All hard-coded regional constants (NZ_GST_RATE = 0.15, NATIVE_TAX_RATES
// lookup by currency_code) have been retired. Tax percent now flows from:
//   1. The item's stored `tax_percent` (historical snapshot at purchase).
//   2. Fallback to the global setting `cdi.settings.taxPercent` when the
//      item has no stored percent (blank/undefined/null).
// This preserves historical items exactly while letting new items pick
// up the current global tax setting.
const _readGlobalTaxPercent = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return 0;
    const raw = window.localStorage.getItem("cdi.settings.taxPercent");
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch { return 0; }
};

const _itemTaxRate = (it) => {
  const stored = it?.tax_percent;
  const hasStored = stored !== null && stored !== undefined && stored !== "";
  const n = hasStored ? Number(stored) : NaN;
  const pct = Number.isFinite(n) && n >= 0 ? n : _readGlobalTaxPercent();
  return pct / 100;
};

const _costBaseNet = (it) => {
  const ex = Number(it?.cost_price_ex_gst || 0);
  if (ex > 0) return ex;
  const rate = _itemTaxRate(it);
  const inc = Number(it?.cost_price_inc_gst || 0);
  if (inc > 0) return inc / (1 + rate);
  const legacy = itemCost(it);
  return legacy > 0 ? legacy / (1 + rate) : 0;
};

const _valueBaseNet = (it) => {
  const ex = Number(it?.estimated_value_ex_gst || 0);
  if (ex > 0) return ex;
  const rate = _itemTaxRate(it);
  const inc = Number(it?.estimated_value_inc_gst || 0);
  if (inc > 0) return inc / (1 + rate);
  // Read value-side legacy fields directly. Never fall back to `itemCost`
  // here — a cost-fallback multiplied by qty would inflate value on
  // missing-estimate case items, and would also introduce a divide-by-zero
  // amplification when combined with the qty multiplier in itemValueNZD.
  const d = it?.item_data || {};
  const fromDynamic = readDynamicNumber(d, "Estimated Value", "Estimated value", "estimated_value");
  if (isFinite(fromDynamic) && fromDynamic > 0) return fromDynamic / (1 + rate);
  const top = Number(it?.estimated_value || 0);
  if (top > 0) return top / (1 + rate);
  return 0;
};

// Bottle count / quantity resolver.
//
// Priority order (2026-02 collection-totals fix):
//   1. `Bottle Count`  — mirrors the ItemDetail display and is now
//      also stamped by the save path alongside Total Bottles.
//   2. `Total Bottles` — canonical analytics key.
//   3. `Quantity Owned` — historical alias.
//
// A stored 0 / "" / null in one key no longer short-circuits the
// resolver — the loop continues to the next candidate. Only a
// strictly-positive integer wins. This fixes the case where an
// earlier bugged save wrote `Total Bottles: 0` while `Bottle Count`
// held the real quantity (e.g. 4) — the item is now correctly
// counted in Dashboard / CollectionDetail rollups instead of
// disappearing behind a bogus zero. When every candidate is 0 /
// missing the function falls back to 0 (unchanged), so items with
// genuinely no stock still contribute 0 to totals.
//
// Per protocol: quantity multiplies BOTH cost and value so the
// invariant `profit = value − cost` holds and all six analytics
// surfaces agree.
export const itemQty = (it) => {
  // Quantity migration Step 5 (2026-02-26) — case math is the single
  // source of truth. Whenever the item carries ANY of the three
  // structured columns, we recompute the total on the fly using the
  // authoritative formula:
  //   total = (cases or 0) * (case_size or 1) + (loose_units or 0)
  // Missing components fall back to the spec defaults so the formula
  // stays stable regardless of which subset the caller persisted. The
  // stored `total_units` column mirrors this formula but is no longer
  // read here directly — that way a stale total_units on an older row
  // can never contradict its live case-math value.
  const cases    = it?.cases;
  const caseSize = it?.case_size;
  const loose    = it?.loose_units;
  const hasAny =
    (cases    !== undefined && cases    !== null && cases    !== "") ||
    (caseSize !== undefined && caseSize !== null && caseSize !== "") ||
    (loose    !== undefined && loose    !== null && loose    !== "");
  if (hasAny) {
    const c  = Number(cases)    || 0;
    const cs = Number(caseSize) || 1; // 0/NaN → 1 per spec default
    const lu = Number(loose)    || 0;
    return c * cs + lu;
  }
  // Legacy fallback for pre-migration items that never persisted any
  // of the structured columns — read the historical item_data keys.
  const d = it?.item_data || {};
  for (const key of ["Bottle Count", "Total Bottles", "Quantity Owned"]) {
    const raw = d[key];
    if (raw === undefined || raw === null || raw === "") continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    if (n > 0) return n;
    // Zero from this key — keep looking; the next key may have the
    // real quantity. All-zero items fall through the loop.
  }
  return 0;
};

export const itemCostNZD = (it) => {
  const baseNet = _costBaseNet(it);
  const fx = _num(it?.fx_rate_purchase, 1);
  const qty = itemQty(it);
  return q2(baseNet * fx * (1 + _itemTaxRate(it)) * qty);
};

export const itemValueNZD = (it) => {
  const baseNet = _valueBaseNet(it);
  // Prefer explicit sale rate when set, otherwise lock to the purchase
  // rate — this keeps collection value stable until the user explicitly
  // updates it or records a sale.
  const fx = _num(it?.fx_rate_sale, 0) || _num(it?.fx_rate_purchase, 1);
  // Sum only per-item estimated value. No cost-mirror fallback, no
  // category averaging — an item with no explicit estimate contributes
  // 0 to the aggregated total.
  if (baseNet === 0) return 0;
  const qty = itemQty(it);
  return q2(baseNet * fx * (1 + _itemTaxRate(it)) * qty);
};

export const itemProfitNZD = (it) => q2(itemValueNZD(it) - itemCostNZD(it));

// ---------------------------------------------------------------------------
// Industry-standard valuation formulas (2026-02) — mirrors backend
// `_compute_item_valuation` in server.py so client + server produce the
// same numbers without an extra round-trip per item.
//
// Inputs required per item:
//   - cost_price_ex_gst
//   - estimated_value_ex_gst
//   - item_data["Total Bottles"] (current on-shelf qty)
//   - sales history for the item (array of Sale rows)
//
// Interpretation of `total_bottles`: the backend defines it as the
// ORIGINAL count. Our sales flow decrements `item_data["Total Bottles"]`
// on each sale, so we reconstruct the original via
// `current_qty + Σsales.qty` — matches the backend helper exactly.
// ---------------------------------------------------------------------------
const _safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export function computeItemValuation(item, salesHistory = []) {
  const it = item || {};
  const history = Array.isArray(salesHistory) ? salesHistory : [];
  const cost_price_ex_gst = _safeNum(it.cost_price_ex_gst);
  const market_valuation = it.market_valuation_ex_gst;
  const manual_valuation = it.manual_valuation_ex_gst;
  const current_qty = itemQty(it);
  const sold_qty = history.reduce((s, r) => s + _safeNum(r.quantity_sold), 0);
  const total_bottles = current_qty + sold_qty;
  const remaining_qty = total_bottles - sold_qty;

  // resolveValuation() — industry-standard hierarchy (2026-02-25).
  // Auto-selected purely from data presence — the persisted
  // `valuation_source` field is treated as a hint only; it can NEVER
  // block a higher-priority value from winning. See backend
  // `_compute_item_valuation` for the matching implementation.
  //   1. Manual  → item.manual_valuation_ex_gst
  //   2. Market  → item.market_valuation_ex_gst
  //   3. Sale    → most recent sale price ex GST
  //   4. Cost    → cost_price_ex_gst
  const FALLBACK_LABEL = "Awaiting Valuation";
  let current_unit_value = 0;
  let valuation_source = FALLBACK_LABEL;

  // 1. Manual
  const mnv = _safeNum(manual_valuation);
  if (mnv > 0) {
    current_unit_value = mnv;
    valuation_source = "Manual";
  }
  // 2. Market
  if (valuation_source === FALLBACK_LABEL) {
    const mv = _safeNum(market_valuation);
    if (mv > 0) {
      current_unit_value = mv;
      valuation_source = "Market";
    }
  }
  // 3. Most Recent Sale — matches backend tie-breaker (sale_date
  // then recorded_at).
  if (valuation_source === FALLBACK_LABEL && history.length > 0) {
    const sorted = [...history].sort((a, b) => {
      const ak = String(a?.sale_date || a?.recorded_at || "");
      const bk = String(b?.sale_date || b?.recorded_at || "");
      return ak < bk ? 1 : ak > bk ? -1 : 0;
    });
    const last = sorted[0] || null;
    const lastPrice = _safeNum(last?.sale_price_ex_gst);
    if (lastPrice > 0) {
      current_unit_value = lastPrice;
      valuation_source = "Most Recent Sale";
    }
  }
  // 4. Buy Price fallback — when no Manual / Market / Sale valuation
  // exists, use cost_price_ex_gst as the current unit value. This
  // ensures the item still contributes a sensible figure to collection
  // value and keeps unrealised P/L and total P/L calculable (both will
  // compute to 0 vs the same cost basis for a never-valued item, so
  // the item is cost-neutral until a real valuation is set).
  if (valuation_source === FALLBACK_LABEL && cost_price_ex_gst > 0) {
    current_unit_value = cost_price_ex_gst;
    valuation_source = "Buy Price";
  }
  // 5. No valuation — leave current_unit_value at 0 and keep the
  // "No valuation set" label so downstream display surfaces (tiles,
  // KPIs, item list) can render a placeholder instead of a numeric 0.
  // (Previously mirrored cost_price_ex_gst; removed 2026-02-26.)

  const current_value = current_unit_value * remaining_qty;
  const realised_PL = history.reduce(
    (s, r) => s + (_safeNum(r.sale_price_ex_gst) - cost_price_ex_gst) * _safeNum(r.quantity_sold),
    0,
  );
  const remaining_cost_basis = cost_price_ex_gst * remaining_qty;
  const unrealised_PL = current_value - remaining_cost_basis;
  const total_PL = realised_PL + unrealised_PL;
  const total_cost = cost_price_ex_gst * total_bottles;
  const PL_percent = total_cost === 0 ? 0 : (total_PL / total_cost) * 100;

  // Canonical valuation-included flag. Derived from `current_unit_value`
  // (any of Manual / Market / Most Recent Sale > 0) rather than the
  // human-readable label — string comparisons on `valuation_source`
  // have been retired platform-wide (2026-02-26). Downstream reducers
  // gate their value / P/L accumulators on this flag; cost accumulators
  // remain unconditional.
  const includeInValuationTotals = current_unit_value > 0;

  return {
    remaining_qty,
    current_unit_value,
    current_value,
    realised_PL,
    unrealised_PL,
    total_PL,
    total_cost,
    PL_percent,
    valuation_source,
    includeInValuationTotals,
  };
}

// Thin adapters — kept alongside the legacy exports above so existing
// callers can migrate one page at a time. All three consume a
// pre-computed valuation object (`computeItemValuation` result) so no
// caller pays the formula cost twice.
export const itemValueV2 = (val) => _safeNum(val?.current_value);
export const itemCostV2 = (val) => _safeNum(val?.total_cost);
export const itemProfitV2 = (val) => _safeNum(val?.total_PL);
